import React, { useEffect, useState, useRef } from "react";

import { useAPI } from "../../hooks/useAPI";
import { useAuth } from "../../contexts/AuthContext";

import { Typography, Box, Button, IconButton } from "@mui/material";
import { Stack } from "@mui/material";
import { Frame } from "../../components/layouts/Frame";
import { Link } from "../../components/global/Link";

import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { DataTable } from "../../components/global/Table/DataTable";
import { DataTableHeadCellData } from "../../components/global/Table/DataTableHeader";
import { QuoteData } from "../../types";

const headCells: DataTableHeadCellData[] = [
  {
    id: "customer_name",
    disablePadding: true,
    label: "Customer Name",
    align: "left",
    sortable: true,
    searchable: true,
  },
  {
    id: "user",
    disablePadding: true,
    label: "User",
    align: "left",
    cellAlign: "left",
    sortable: true,
    searchable: true,
  },
  {
    id: "reference",
    disablePadding: true,
    label: "Reference",
    align: "left",
    cellAlign: "left",
  },
  {
    id: "date",
    disablePadding: true,
    label: "Date",
    align: "left",
    cellAlign: "left",
  },
  {
    id: "value",
    disablePadding: true,
    label: "Value (£)",
    align: "left",
    cellAlign: "left",
  },
  {
    id: "actions",
    disablePadding: false,
    label: "Actions",
    align: "left",
    cellAlign: "left",
    width: "50px",
  },
];

interface QuoteTableData {
  customer_name: any;
  user: any;
  reference: any;
  date: any;
  value: any;
  actions: JSX.Element;
}

export const Dashboard = () => {
  const isLoading = useRef(false);

  const { fetchData } = useAPI();
  const { isAuthenticated } = useAuth();
  const [sIsLoading, setSIsLoading] = useState(false);
  const [rows, setRows] = useState<QuoteTableData[]>([]);
  const [quotes, setQuotes] = useState<QuoteData[]>([]);

  const getRows = () => {
    const result: QuoteTableData[] = [];

    quotes.forEach(quote => {
      const date = new Date(quote.created_at);

      const formattedDate = new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      }).format(date);

      result.push({
        customer_name: (
          <Typography
            sx={{
              color: quote?.status == "draft" ? "grey" : "#000000",
            }}>
            {quote?.customer_first_name ?? ""} {quote?.customer_last_name ?? ""}
          </Typography>
        ),
        user: (
          <Typography
            sx={{
              color: quote?.status == "draft" ? "grey" : "#000000",
            }}>
            {quote?.user?.name
              .split(" ")
              .map((word: string) => word[0]?.toUpperCase())
              .join("")}
          </Typography>
        ),
        reference: (
          <Typography
            sx={{
              color: quote?.status == "draft" ? "grey" : "#000000",
            }}>
            {quote.reference}
          </Typography>
        ),
        date: (
          <Typography
            sx={{
              color: quote?.status == "draft" ? "grey" : "#000000",
            }}>
            {formattedDate}
          </Typography>
        ),
        value: (
          <Typography
            sx={{
              color: quote?.status == "draft" ? "grey" : "#000000",
            }}>
            £{parseFloat(quote.lowest).toLocaleString("en")} ~ £
            {parseFloat(quote.highest).toLocaleString("en")}
          </Typography>
        ),
        actions: (
          <Box
            sx={{
              display: "flex",
            }}>
            <IconButton aria-label="edit">
              <EditIcon />
            </IconButton>
            <IconButton aria-label="delete">
              <VisibilityIcon />
            </IconButton>
          </Box>
        ),
      });
    });

    return result;
  };

  const refetchQuotes = async () => {
    setSIsLoading(true);
    isLoading.current = true;
    const { errors, data } = await fetchData("/quote/get");
    setQuotes(data ?? []);
    isLoading.current = false;
    setSIsLoading(false);
  };

  useEffect(() => {
    if (isAuthenticated && !isLoading.current) {
      refetchQuotes();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setRows(getRows());
  }, [quotes]);

  return (
    <Frame title="Dashboard">
      <Stack>
        <Stack
          display="flex"
          justifyContent="flex-end"
          flexDirection="row"
          mb={2}>
          <Button variant="contained">Appointments</Button>
        </Stack>
        <DataTable
          title="Dashboard"
          headCells={headCells}
          rows={rows}
          isLoading={sIsLoading}
          hideIdCheckbox={true}
          hideDeleteButton={true}
        />
      </Stack>
    </Frame>
  );
};
